import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useUserStore } from '../stores/userStore';
import { useLanguage } from '../hooks/useLanguage';
import { userApi } from '../lib/api';
import { StatsGrid } from '../components/StatsCard';
import { clsx } from 'clsx';

export function ProfilePage() {
  const { user, stats } = useUserStore();
  const { t } = useLanguage();

  const { data: submissions } = useQuery({
    queryKey: ['submissions', user?.telegram_id],
    queryFn: () => userApi.getSubmissions(user!.telegram_id),
    enabled: !!user,
  });

  const { data: transactions } = useQuery({
    queryKey: ['transactions', user?.telegram_id],
    queryFn: () => userApi.getTransactions(user!.telegram_id),
    enabled: !!user,
  });

  return (
    <div className="p-4 space-y-4">
      {/* Profile Header */}
      <div className="bg-tg rounded-xl p-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center text-2xl font-bold text-brand-primary">
            {user?.first_name?.[0] || '?'}
          </div>
          <div>
            <h1 className="text-xl font-bold text-tg">
              {user?.first_name} {user?.last_name || ''}
            </h1>
            {user?.username && (
              <p className="text-tg-hint">@{user.username}</p>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && <StatsGrid stats={stats} />}

      {/* Wallet Link */}
      <Link
        to="/wallet"
        className="block bg-tg rounded-xl p-5"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-tg">{t('wallet')}</h2>
            <p className="text-sm text-tg-hint">
              {user?.wallet_address
                ? `${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)}`
                : t('pending')}
            </p>
          </div>
          <span className="text-tg-hint">→</span>
        </div>
      </Link>

      {/* Recent Submissions */}
      <div className="bg-tg rounded-xl p-5">
        <h2 className="font-semibold text-tg mb-4">{t('recent_submissions')}</h2>
        {(submissions as any[])?.length === 0 ? (
          <p className="text-tg-hint text-sm">{t('no_submissions')}</p>
        ) : (
          <div className="space-y-3">
            {(submissions as any[])?.slice(0, 5).map((sub: any) => (
              <div key={sub.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-tg">{sub.task_title}</p>
                  <p className="text-xs text-tg-hint">{sub.platform}</p>
                </div>
                <span
                  className={clsx(
                    'px-2 py-1 rounded text-xs font-medium',
                    sub.status === 'approved' && 'bg-green-100 text-green-700',
                    sub.status === 'rejected' && 'bg-red-100 text-red-700',
                    sub.status === 'pending' && 'bg-yellow-100 text-yellow-700'
                  )}
                >
                  {t(`status_${sub.status}` as any)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="bg-tg rounded-xl p-5">
        <h2 className="font-semibold text-tg mb-4">{t('recent_transactions')}</h2>
        {(transactions as any[])?.length === 0 ? (
          <p className="text-tg-hint text-sm">{t('no_transactions')}</p>
        ) : (
          <div className="space-y-3">
            {(transactions as any[])?.slice(0, 5).map((tx: any) => (
              <div key={tx.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-tg capitalize">{tx.type}</p>
                  <p className="text-xs text-tg-hint">
                    {new Date(tx.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={clsx(
                    'font-medium',
                    tx.amount_usd > 0 ? 'text-green-600' : 'text-red-600'
                  )}
                >
                  {tx.amount_usd > 0 ? '+' : ''}${(tx.amount_usd / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
